import React, { useState, useCallback } from 'react';
import { Question, DisplayLogic, DisplayCondition } from '../types';
import { PlusIcon, TrashIcon } from './Icons';

interface DisplayLogicConfigProps {
  currentQuestion: Question;
  allQuestions: Question[];
  value: DisplayLogic | null;
  onChange: (logic: DisplayLogic | null) => void;
}

const DisplayLogicConfig: React.FC<DisplayLogicConfigProps> = ({
  currentQuestion,
  allQuestions,
  value,
  onChange,
}) => {
  const [enabled, setEnabled] = useState(!!value);
  const [operator, setOperator] = useState<'AND' | 'OR'>(value?.operator || 'AND');
  const [conditions, setConditions] = useState<DisplayCondition[]>(
    value?.conditions || [{ sourceQuestionId: 0, conditionType: '', value: '' }]
  );

  // 获取当前问题之前的所有问题（基于在列表中的位置，而不是orderNum）
  const currentIndex = allQuestions.findIndex(q => q.id === currentQuestion.id);
  const availableQuestions = currentIndex > 0 ? allQuestions.slice(0, currentIndex) : [];

  // 通知父组件变更（命令式调用，不再依赖 useEffect）
  const notifyChange = useCallback((
    isEnabled: boolean,
    op: 'AND' | 'OR',
    conds: DisplayCondition[]
  ) => {
    if (!isEnabled) {
      onChange(null);
    } else {
      const filtered = conds.filter(c => c.sourceQuestionId > 0 && c.conditionType);
      onChange({ operator: op, conditions: filtered });
    }
  }, [onChange]);

  const addCondition = () => {
    const newConditions = [...conditions, { sourceQuestionId: 0, conditionType: '', value: '' }];
    setConditions(newConditions);
    notifyChange(enabled, operator, newConditions);
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
    notifyChange(enabled, operator, newConditions);
  };

  const updateCondition = (index: number, field: keyof DisplayCondition, val: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: val };
    setConditions(newConditions);
    notifyChange(enabled, operator, newConditions);
  };

  const getConditionTypes = (questionType: string) => {
    switch (questionType) {
      case 'SINGLE_CHOICE':
        return [
          { value: 'OPTION_SELECTED', label: '选择了选项' },
          { value: 'OPTION_NOT_SELECTED', label: '未选择选项' },
        ];
      case 'MULTIPLE_CHOICE':
        return [
          { value: 'OPTIONS_CONTAINS_ANY', label: '包含任意选项' },
          { value: 'OPTIONS_CONTAINS_ALL', label: '包含所有选项' },
          { value: 'OPTIONS_NOT_CONTAINS', label: '不包含选项' },
        ];
      case 'TEXT':
        return [
          { value: 'TEXT_EQUALS', label: '文本等于' },
          { value: 'TEXT_CONTAINS', label: '文本包含' },
          { value: 'TEXT_NOT_EMPTY', label: '文本不为空' },
        ];
      case 'SLIDER':
        return [
          { value: 'SLIDER_GREATER_THAN', label: '值大于' },
          { value: 'SLIDER_LESS_THAN', label: '值小于' },
          { value: 'SLIDER_RANGE', label: '值在范围内' },
        ];
      case 'MATRIX':
        return [
          { value: 'MATRIX_CELL_SELECTED', label: '选择了特定单元格' },
          { value: 'MATRIX_ROW_SELECTED', label: '某行选择了特定列' },
          { value: 'MATRIX_SCORE_RANGE', label: '矩阵总分在范围内' },
          { value: 'MATRIX_ALL_ANSWERED', label: '所有行都已回答' },
        ];
      default:
        return [];
    }
  };

  const renderConditionValue = (condition: DisplayCondition, index: number) => {
    const sourceQuestion = allQuestions.find(q => q.id === condition.sourceQuestionId);
    if (!sourceQuestion) return null;

    const { conditionType } = condition;

    // 不需要输入值的条件
    if (['TEXT_NOT_EMPTY', 'MATRIX_ALL_ANSWERED', 'MATRIX_ANY_ANSWERED'].includes(conditionType)) {
      return null;
    }

    // 单选题 - 选择选项
    if (sourceQuestion.type === 'SINGLE_CHOICE' && conditionType.includes('OPTION')) {
      return (
        <select
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
        >
          <option value="">选择选项</option>
          {sourceQuestion.options.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.content}</option>
          ))}
        </select>
      );
    }

    // 多选题 - 选择选项（简化版，使用逗号分隔的ID）
    if (sourceQuestion.type === 'MULTIPLE_CHOICE') {
      return (
        <input
          type="text"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="输入选项ID，用逗号分隔，如: 1,2,3"
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
        />
      );
    }

    // 文本题
    if (sourceQuestion.type === 'TEXT') {
      return (
        <input
          type="text"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="输入文本值"
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
        />
      );
    }

    // 滑块题
    if (sourceQuestion.type === 'SLIDER') {
      if (conditionType === 'SLIDER_RANGE') {
        return (
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder='JSON格式，如: {"min":10,"max":50}'
            value={condition.value || ''}
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
          />
        );
      }
      return (
        <input
          type="number"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="输入数值"
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
        />
      );
    }

    // 矩阵题 - 分数范围
    if (sourceQuestion.type === 'MATRIX' && conditionType === 'MATRIX_SCORE_RANGE') {
      let minScore = '';
      let maxScore = '';
      try {
        const parsed = JSON.parse(condition.value || '{}');
        minScore = parsed.minScore !== undefined ? String(parsed.minScore) : '';
        maxScore = parsed.maxScore !== undefined ? String(parsed.maxScore) : '';
      } catch {}

      return (
        <div className="flex-1 flex items-center gap-2">
          <input
            type="number"
            className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder="最低分"
            value={minScore}
            onChange={(e) => {
              const val = e.target.value;
              const obj: any = {};
              if (val !== '') obj.minScore = Number(val);
              if (maxScore !== '') obj.maxScore = Number(maxScore);
              updateCondition(index, 'value', JSON.stringify(obj));
            }}
          />
          <span className="text-gray-500 text-sm">~</span>
          <input
            type="number"
            className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder="最高分"
            value={maxScore}
            onChange={(e) => {
              const val = e.target.value;
              const obj: any = {};
              if (minScore !== '') obj.minScore = Number(minScore);
              if (val !== '') obj.maxScore = Number(val);
              updateCondition(index, 'value', JSON.stringify(obj));
            }}
          />
          <span className="text-xs text-gray-400 whitespace-nowrap">分</span>
        </div>
      );
    }

    // 矩阵题 - 其他条件
    if (sourceQuestion.type === 'MATRIX') {
      return (
        <input
          type="text"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder='JSON格式，如: {"row":"服务态度","column":"满意"}'
          value={condition.value || ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
        />
      );
    }

    return null;
  };

  if (availableQuestions.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        此题是第一题，无法设置显示条件
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={enabled}
            onChange={(e) => {
              const newEnabled = e.target.checked;
              setEnabled(newEnabled);
              notifyChange(newEnabled, operator, conditions);
            }}
          />
          <span className="ml-2 text-sm font-medium text-gray-700">启用条件显示</span>
        </label>

        {enabled && conditions.length > 1 && (
          <select
            className="px-3 py-1 border border-gray-300 rounded text-sm"
            value={operator}
            onChange={(e) => {
              const newOp = e.target.value as 'AND' | 'OR';
              setOperator(newOp);
              notifyChange(enabled, newOp, conditions);
            }}
          >
            <option value="AND">满足所有条件</option>
            <option value="OR">满足任意条件</option>
          </select>
        )}
      </div>

      {enabled && (
        <div className="space-y-3">
          {conditions.map((condition, index) => {
            const sourceQuestion = allQuestions.find(q => q.id === condition.sourceQuestionId);
            const conditionTypes = sourceQuestion ? getConditionTypes(sourceQuestion.type) : [];

            return (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-3 gap-2">
                  {/* 选择来源问题 */}
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={condition.sourceQuestionId}
                    onChange={(e) => {
                      const newConditions = [...conditions];
                      newConditions[index] = { ...newConditions[index], sourceQuestionId: Number(e.target.value), conditionType: '', value: '' };
                      setConditions(newConditions);
                      notifyChange(enabled, operator, newConditions);
                    }}
                  >
                    <option value={0}>选择问题</option>
                    {availableQuestions.map((q, idx) => (
                      <option key={q.id} value={q.id}>
                        第{idx + 1}题: {q.title}
                      </option>
                    ))}
                  </select>

                  {/* 选择条件类型 */}
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={condition.conditionType}
                    onChange={(e) => {
                      const newConditions = [...conditions];
                      newConditions[index] = { ...newConditions[index], conditionType: e.target.value, value: '' };
                      setConditions(newConditions);
                      notifyChange(enabled, operator, newConditions);
                    }}
                    disabled={!condition.sourceQuestionId}
                  >
                    <option value="">选择条件</option>
                    {conditionTypes.map(ct => (
                      <option key={ct.value} value={ct.value}>{ct.label}</option>
                    ))}
                  </select>

                  {/* 条件值 */}
                  {condition.conditionType && (
                    <div className="col-span-1">
                      {renderConditionValue(condition, index)}
                    </div>
                  )}
                </div>

                {/* 删除按钮 */}
                {conditions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="p-2 text-danger-600 hover:bg-danger-50 rounded"
                  >
                    <TrashIcon size={20} />
                  </button>
                )}
              </div>
            );
          })}

          {/* 添加条件按钮 */}
          <button
            type="button"
            onClick={addCondition}
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
          >
            <PlusIcon size={16} />
            添加条件
          </button>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>说明：</strong>
            当上述条件满足时，此问题将显示给用户。否则，问题将被隐藏。
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayLogicConfig;
